import { Suspense, useEffect, useRef, useState } from "react";
import styled, { css } from "styled-components";
import { motion } from "framer-motion";
import { Outlet, useLocation } from "react-router-dom";
import Header from "../Header/Header";
import Footer from "../Footer/Footer";
import TabsComponent from "../utils/TabsComponent/TabsComponent";
import HomeHero from "../HomeHero/HomeHero";
import { useSelector } from "react-redux";
import {
  selectCategories,
  selectExploreCollections,
  selectSelectedCollection,
} from "../../store/slices/model/slice";
import modelService from "../../api/services/model";
import { CREATE_IMAGE_MODEL_NAME } from "../../utils/constants";
import gamesBgImage from "../../assets/images/background.png";
// import {
//   BASIC_PLAN_NAME,
//   PREMIUM_PLAN_NAME,
//   PRO_PLAN_NAME,
// } from "../../utils/constants";
// import { selectUser } from "../../store/slices/user/slice";
// import PlayboxBannerImagePhoto from "../../assets/images/placeholder.webp";
//
// import {
//   PlayboxBanner,
//   PlayboxBannerImage,
//   PlayboxBannerImageSmall,
//   PlayboxBannerImageWrapper,
//   PlayboxBannerContent,
//   PlayboxBannerTitle,
//   PlayboxBannerText,
//   PlayboxBannerChevron,
// } from "../../screens/HomePage/HomePage.styles";

const TabsLayout = () => {
  const location = useLocation();
  const isHome = location.pathname === "/";

  const heroRef = useRef(null);
  const [heroHeight, setHeroHeight] = useState(isHome ? "auto" : 0);

  const exploreData = useSelector(selectExploreCollections);
  const categories = useSelector(selectCategories);
  const selectedCollection = useSelector(selectSelectedCollection);
  const [fetchedCreateImageCollection, setFetchedCreateImageCollection] =
    useState(null);

  const defaultCreateImageCollection =
    exploreData.data?.find(
      (item) => item.modelType === CREATE_IMAGE_MODEL_NAME,
    ) ||
    (selectedCollection?.modelType === CREATE_IMAGE_MODEL_NAME
      ? selectedCollection
      : null) ||
    fetchedCreateImageCollection;

  useEffect(() => {
    if (defaultCreateImageCollection || fetchedCreateImageCollection) return;

    const pictureCategory = categories?.data?.find((c) => c.name === "Picture");
    const categoryId = pictureCategory?._id || categories?.data?.[0]?._id || "";

    let cancelled = false;
    (async () => {
      try {
        const result = await modelService.getExploreCollections({
          page: 1,
          search: "",
          categoryId,
        });
        if (cancelled) return;
        const found = (result?.data || []).find(
          (item) => item.modelType === CREATE_IMAGE_MODEL_NAME,
        );
        if (found) setFetchedCreateImageCollection(found);
      } catch {
        // silent — generation will surface error if user clicks
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [categories, defaultCreateImageCollection, fetchedCreateImageCollection]);

  useEffect(() => {
    const node = heroRef.current;
    if (!node) return;

    if (isHome) {
      const measured = node.scrollHeight;
      setHeroHeight(measured);
      const id = window.setTimeout(() => setHeroHeight("auto"), 550);
      return () => window.clearTimeout(id);
    } else {
      const measured = node.scrollHeight;
      setHeroHeight(measured);
      requestAnimationFrame(() => setHeroHeight(0));
    }
  }, [isHome]);

  // const user = useSelector(selectUser);
  //
  // const isProEnabled =
  //   [PRO_PLAN_NAME, PREMIUM_PLAN_NAME].includes(user?.subscription?.name) &&
  //   user?.subscription?.endDate &&
  //   new Date(user?.subscription?.endDate) > new Date();
  //
  // const isBasicEnabled =
  //   user?.subscription?.name === BASIC_PLAN_NAME &&
  //   user?.subscription?.endDate &&
  //   new Date(user?.subscription?.endDate) > new Date();
  //
  // const isSubscribed = isBasicEnabled || isProEnabled;
  //
  // const playboxBannerContent =
  //   isSubscribed && (
  //     <PlayboxBanner
  //       href="https://playbox.com"
  //       target="_blank"
  //       rel="noopener noreferrer"
  //     >
  //       <PlayboxBannerImageWrapper>
  //         <PlayboxBannerImage src={PlayboxBannerImagePhoto} alt="" />
  //         <PlayboxBannerImageSmall src={PlayboxBannerImagePhoto} alt="" />
  //       </PlayboxBannerImageWrapper>
  //       <PlayboxBannerContent>
  //         <PlayboxBannerTitle>More fun is waiting</PlayboxBannerTitle>
  //         <span>
  //           <PlayboxBannerText>Log in to your account on </PlayboxBannerText>
  //           <PlayboxBannerText $underline>Playbox.com</PlayboxBannerText>
  //         </span>
  //       </PlayboxBannerContent>
  //       <PlayboxBannerChevron
  //         xmlns="http://www.w3.org/2000/svg"
  //         width="20"
  //         height="20"
  //         viewBox="0 0 20 20"
  //         fill="none"
  //       >
  //         <path
  //           d="M7.5 15L12.5 10L7.5 5"
  //           stroke="white"
  //           strokeWidth="1.5"
  //           strokeLinecap="round"
  //           strokeLinejoin="round"
  //         />
  //       </PlayboxBannerChevron>
  //     </PlayboxBanner>
  //   );

  const isGames = location.pathname.startsWith("/games");

  return (
    <Container>
      {isGames && (
        <GamesBg
          aria-hidden
          style={{
            // Solid backdrop colour through the header + tabs band (top ~150px),
            // then fade into the image; fade back to solid at the bottom of the
            // viewport so the footer area blends instead of cutting off hard.
            // Colours track the fixed blue gradient on body::before (both are
            // viewport-aligned) so the fade edges stay seamless.
            backgroundImage: `linear-gradient(180deg, rgb(19,35,58) 0, rgb(13,21,33) 150px, rgba(10,26,47,0.5) 340px, rgba(10,26,47,0.5) calc(100% - 190px), rgb(3,5,8) 100%), url(${gamesBgImage})`,
          }}
        />
      )}
      <Header />
      <HeroCollapser
        ref={heroRef}
        style={{
          height: heroHeight === "auto" ? "auto" : `${heroHeight}px`,
        }}
        $visible={isHome}
        aria-hidden={!isHome}
      >
        <HomeHero defaultCollection={defaultCreateImageCollection} />
      </HeroCollapser>
      <TabsBar $noHero={!isHome}>
        <TabsComponent inline />
      </TabsBar>
      <ContentWrap>
        {/* Lazy route chunks (games) suspend here so the header and
            tabs stay visible while the chunk downloads. */}
        <Suspense
          fallback={
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flex: 1,
                minHeight: "40vh",
              }}
            >
              <l-line-spinner
                size="28"
                stroke="1.5"
                speed="1.5"
                color="white"
              />
            </div>
          }
        >
          {/* Subtle fade + rise on every route change so navigation feels
              smooth instead of a hard cut. Keyed by pathname to replay. */}
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            style={{
              display: "flex",
              flexDirection: "column",
              flex: 1,
              width: "100%",
            }}
          >
            <Outlet />
          </motion.div>
        </Suspense>
      </ContentWrap>
      <Footer />
    </Container>
  );
};

export default TabsLayout;

// Full-viewport games background. z-index:-1 inside the isolated Container puts
// it above the body gradient but behind the fixed header, tabs and content.
const GamesBg = styled.div`
  position: fixed;
  inset: 0;
  z-index: -1;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  pointer-events: none;
`;

const Container = styled.div`
  display: flex;
  position: relative;
  /* Own stacking context so the games background (z-index:-1) sits ABOVE the
     body gradient but behind the header, tabs and content. */
  isolation: isolate;
  flex-direction: column;
  align-items: center;
  min-height: 100dvh;
  /* Transparent so the fixed blue gradient on body::before shows through. */
  background-color: transparent;
  color: white;
  width: 100%;
  padding-top: 64px;
  transition:
    background-color 0.3s ease,
    color 0.3s ease;
`;

const HeroCollapser = styled.div`
  width: 100%;
  overflow: hidden;
  transition:
    height 0.5s cubic-bezier(0.22, 1, 0.36, 1),
    opacity 0.45s cubic-bezier(0.22, 1, 0.36, 1),
    transform 0.5s cubic-bezier(0.22, 1, 0.36, 1);

  ${(props) =>
    props.$visible
      ? css`
          opacity: 1;
          transform: translateY(0);
        `
      : css`
          opacity: 0;
          transform: translateY(-8px);
          pointer-events: none;
        `}
`;

const TabsBar = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  /* When the hero is collapsed (non-Explore tabs) the bar sits right under
     the header — give it extra breathing room on top. */
  padding: ${(props) => (props.$noHero ? "28px 0 12px" : "12px 0")};
  transition: padding 0.3s ease;
`;

const ContentWrap = styled.div`
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
  /* Never allow sideways scroll — game boards (e.g. the spin wheel glow) can
     bleed a few px past the viewport on phones. clip (not hidden) doesn't
     create a scroll container, so window scroll + position:fixed stay intact. */
  overflow-x: clip;
`;
